<template>
  <LazySection
    :is-height-fixed="!isFetched"
    :placeholder-height="{ desktop: 500, mobile: 300 }"
  >
    <div
      v-if="citiesCount > 2"
      v-city-around-list-view-tracker="getCityAroundListViewEventParams"
      class="wrap"
    >
      <section class="city-around">
        <h3
          v-if="props.isCityAroundPage"
          class="city-around__title"
        >
          {{ title }}
        </h3>
        <a
          v-else
          class="city-around__title"
          :href="aroundTitleLink"
        >
          {{ title }}
          <span class="arrow">
            <ArrowRightIcon />
          </span>
        </a>
        <div class="city-around__list">
          <CityAroundCard
            v-for="(city, idx) in displayedCities"
            :key="idx"
            :default-sogu-fields="defaultSoguFields"
            :city-name="city.name_ru"
            :experience-count="city.excursions_count || 0"
            :image="city.image"
            :url="aroundCityLink(city.url || '')"
          />
        </div>

        <ui-button
          v-if="hasMoreCities"
          class="city-around__button"
          data-test="city-around-button"
          :variant="UiButtonVariant.OUTLINE"
          :color-mode="UiButtonColorMode.NEUTRAL"
          :size="UiButtonSize.M"
          :text="t('section.button')"
          @click="showMoreCities"
        >
        </ui-button>
      </section>
    </div>
  </LazySection>
</template>

<script setup lang="ts">
  import { UiButton, UiButtonColorMode, UiButtonSize, UiButtonVariant } from '@experience/core-button-ui';
  import CityAroundCard from './CityAroundCard.vue';
  import ru from '../i18n/ru.json';
  import { useI18n } from '@experience/i18n';
  import { ArrowRightIcon } from '@/shared/ui/icons/arrowRightIcon';
  import type { CityInfo, WebCitiesAround } from '@/shared/api/experience';
  import { useAroundCities } from '@/entities/city/api/useAroundCities.ts';
  import { DISPLAY_LIMIT, LOAD_INCREMENT } from '../lib/consts.ts';
  import { useScreenSize, withoutDomain } from '@experience/shared';
  import { withMfsDomain } from '@/shared/lib/withMfsDomain';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import { trackEvent, useViewTracker } from '@/entities/sogu';
  import { makeFullLink } from '@/shared/lib/makeFullLink';
  import { LazySection } from '@/shared/ui/LazySections';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface CityAroundProps {
    cityInfo: CityInfo | undefined;
    isCityAroundPage?: boolean;
    citySlug: string;
    defaultSoguFields?: ContextGeoSubject;
  }

  const props = withDefaults(defineProps<CityAroundProps>(), {
    isCityAroundPage: false,
  });

  const { aroundCities: cities, isFetched } = useAroundCities(computed(() => props.cityInfo?.id));
  const { isMobile } = useScreenSize();

  const title = computed(() => t('section.title', { fromObjPhrase: props.cityInfo?.from_obj_phrase }));
  const citiesCount = computed(() => cities.value?.length ?? 0);
  const displayLimit = ref(DISPLAY_LIMIT);
  const displayedCities = computed<WebCitiesAround[]>(() =>
    props.isCityAroundPage || isMobile.value ? cities.value || [] : cities.value?.slice(0, displayLimit.value) || [],
  );
  const hasMoreCities = computed(() => {
    if (props.isCityAroundPage) {
      return false;
    }

    return displayLimit.value < citiesCount.value;
  });
  const showMoreCities = () => {
    if (!props.isCityAroundPage) {
      displayLimit.value = Math.min(displayLimit.value + LOAD_INCREMENT, citiesCount.value);
      trackEvent('listing:around:show_more.click', getCityAroundListViewEventParams());
    }
  };

  const aroundTitleLink = computed(() => makeFullLink(`/around/${props.citySlug}/`));
  const aroundCityLink = (url: string) => {
    return withMfsDomain(withoutDomain(url));
  };

  const getCityAroundListViewEventParams = () => ({ ...props.defaultSoguFields?.soguStamp });
  const vCityAroundListViewTracker = useViewTracker('listing:around.view');

  const gridRows = computed(() => {
    return displayedCities.value?.length > 1 ? '1fr 1fr' : '1fr';
  });
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .wrap {
    padding-right: 0;

    @include adaptivity.mf-tablet {
      padding-right: 20px;

      .city-around__title {
        font-size: var(--global-typography-size-700);
        line-height: var(--global-typography-line-height-500);
      }

      .city-around__list {
        grid-template-columns: repeat(5, 1fr);
        padding-right: 0;
      }
    }

    @include adaptivity.mf-laptop {
      .city-around {
        gap: 24px;
        margin-top: 80px;
      }

      .city-around__list {
        grid-template-rows: auto;
        grid-auto-flow: row;
        gap: 16px;
      }

      .city-around__button.button {
        --btn-width: 598px;
        display: block;
        margin: 0 auto;
      }

      .city-around__title {
        font-size: var(--global-typography-size-800);
        line-height: var(--global-typography-line-height-800);
      }
    }
  }

  .city-around {
    display: grid;
    gap: 16px;
    margin-top: 56px;
  }

  .city-around__title {
    justify-self: start;
    margin: 0;
    padding-right: 20px;
    color: var(--alias-color-text-default);
    font-weight: var(--global-typography-weight-extra);
    font-size: var(--global-typography-size-600);
    line-height: var(--global-typography-line-height-400);
    text-decoration: none;

    .arrow {
      position: relative;

      .icon {
        position: absolute;
        top: 55%;
        left: 4px;
        width: 20px;
        height: 20px;
        fill: var(--alias-color-icon-default);
        transform: translateY(-50%);
      }
    }

    @include adaptivity.tablet {
      @include typography.base-600-500-extra;
    }
  }

  .city-around__list {
    display: grid;
    grid-template-rows: v-bind('gridRows');
    grid-auto-columns: 140px;
    grid-auto-flow: column;
    gap: 8px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-right: 20px;
    scrollbar-width: none;
    scroll-snap-type: x mandatory;
  }

  .city-around-card {
    scroll-snap-align: start;
  }

  .city-around__button.button {
    display: none;
  }
</style>
